import { createDocumentCustomField } from "@/api/documents";
import { getApiErrorMessage, IApiAxiosError } from "@/api/api.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { clampPageInput } from "@/utils/fields";
import {
    Popover,
    PopoverContent,
    PopoverHeader,
    PopoverTitle,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";

export interface AddCustomFieldButtonProps {
    documentId: number;
    policyTypeId: number;
    /** Current visible PDF page (1-indexed), used as default page_number */
    currentPage?: number;
    /** Total pages in the PDF document */
    totalPages?: number;
}

interface FormState {
    fieldName: string;
    propertyName: string;
    value: string;
    pageNumber: string;
}

function makeEmptyForm(currentPage?: number): FormState {
    return {
        fieldName: "",
        propertyName: "",
        value: "",
        pageNumber: currentPage ? String(currentPage) : "",
    };
}

interface FormErrors {
    fieldName: string | null;
    propertyName: string | null;
    value: string | null;
    pageNumber: string | null;
}

const NO_ERRORS: FormErrors = {
    fieldName: null,
    propertyName: null,
    value: null,
    pageNumber: null,
};

export function AddCustomFieldButton({
    documentId,
    policyTypeId,
    currentPage,
    totalPages,
}: AddCustomFieldButtonProps) {
    const queryClient = useQueryClient();

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState<FormState>(makeEmptyForm(currentPage));
    const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);

    const fieldNameId = useId();
    const propNameId = useId();
    const valueId = useId();
    const pageId = useId();

    const fieldNameRef = useRef<HTMLInputElement>(null);
    const propNameRef = useRef<HTMLInputElement>(null);
    const valueRef = useRef<HTMLInputElement>(null);

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            const pageNum = form.pageNumber.trim()
                ? parseInt(form.pageNumber, 10)
                : null;

            return createDocumentCustomField(documentId, {
                name: form.fieldName.trim(),
                policy_type_id: policyTypeId,
                properties: [
                    {
                        name: form.propertyName.trim(),
                        value: form.value.trim() || null,
                        page_number: pageNum,
                    },
                ],
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
            handleClose();
        },
        onError: (error: IApiAxiosError) => {
            toast.error(
                getApiErrorMessage(error, "Failed to add custom field")
            );
        },
    });

    function handleOpen(nextOpen: boolean) {
        if (nextOpen) {
            setForm(makeEmptyForm(currentPage));
            setErrors(NO_ERRORS);
        }
        setOpen(nextOpen);
    }

    function handleClose() {
        setOpen(false);
        setForm(makeEmptyForm(currentPage));
        setErrors(NO_ERRORS);
    }

    function validate(): boolean {
        const next: FormErrors = { ...NO_ERRORS };
        let valid = true;

        if (!form.fieldName.trim()) {
            next.fieldName = "Field name is required";
            valid = false;
        }
        if (!form.propertyName.trim()) {
            next.propertyName = "Property name is required";
            valid = false;
        }
        if (!form.value.trim()) {
            next.value = "Value is required";
            valid = false;
        }
        if (form.pageNumber.trim()) {
            const num = parseInt(form.pageNumber, 10);
            if (isNaN(num) || num < 1) {
                next.pageNumber = "Must be at least 1";
                valid = false;
            } else if (totalPages && num > totalPages) {
                next.pageNumber = `Max ${totalPages}`;
                valid = false;
            }
        }

        setErrors(next);
        if (!valid) {
            if (next.fieldName) fieldNameRef.current?.focus();
            else if (next.propertyName) propNameRef.current?.focus();
            else if (next.value) valueRef.current?.focus();
        }
        return valid;
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (validate()) mutate();
    }

    function updateField(key: keyof FormState, value: string) {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: null }));
        }
    }

    // Wrapping div is load-bearing: when the popover opens, Base UI's
    // <Popover.Trigger> injects sibling <span data-base-ui-focus-guard>
    // elements around the trigger button. Those spans are `position: fixed`
    // so they don't take flow space, but they DO flip the trigger button's
    // `:last-child` status. Tailwind v4's `space-y-*` adds `margin-block-end`
    // to `:not(:last-child)`, so if this component renders directly inside a
    // `space-y-*` container, the trigger gains a 16px margin-bottom only
    // while the popover is open. The wrapper hides the focus guards from
    // the parent's `:last-child` selector.
    return (
        <div>
            <Popover open={open} onOpenChange={handleOpen}>
                <PopoverTrigger
                    render={
                        <Button
                            variant="outline"
                            size="sm"
                            aria-label="Add custom field"
                            className="border-muted-foreground/40 hover:border-muted-foreground hover:bg-muted/30 text-muted-foreground hover:text-foreground w-full justify-center gap-1.5 border-dashed"
                        />
                    }
                >
                    <PlusIcon data-icon="inline-start" />
                    Add custom field
                </PopoverTrigger>

                <PopoverContent
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    className="w-[320px] p-4"
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.stopPropagation();
                            handleClose();
                        }
                    }}
                >
                    <PopoverHeader className="mb-3">
                        <PopoverTitle className="text-sm font-semibold">
                            Add Custom Field
                        </PopoverTitle>
                    </PopoverHeader>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor={fieldNameId}>
                                    Field Name
                                    <span
                                        aria-hidden="true"
                                        className="text-destructive ml-0.5"
                                    >
                                        *
                                    </span>
                                </Label>
                                <Input
                                    ref={fieldNameRef}
                                    id={fieldNameId}
                                    placeholder="e.g. General Aggregate"
                                    value={form.fieldName}
                                    onChange={(e) =>
                                        updateField("fieldName", e.target.value)
                                    }
                                    disabled={isPending}
                                    aria-invalid={errors.fieldName !== null}
                                    aria-describedby={
                                        errors.fieldName
                                            ? `${fieldNameId}-error`
                                            : undefined
                                    }
                                    autoFocus
                                    autoComplete="off"
                                />
                                {errors.fieldName && (
                                    <p
                                        id={`${fieldNameId}-error`}
                                        role="alert"
                                        className="text-destructive text-xs"
                                    >
                                        {errors.fieldName}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-2 pt-0.5">
                                <div className="bg-border h-px flex-1" />
                                <span className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                                    First Property
                                </span>
                                <div className="bg-border h-px flex-1" />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor={propNameId}>
                                    Property Name
                                    <span
                                        aria-hidden="true"
                                        className="text-destructive ml-0.5"
                                    >
                                        *
                                    </span>
                                </Label>
                                <Input
                                    ref={propNameRef}
                                    id={propNameId}
                                    placeholder="e.g. Limit"
                                    value={form.propertyName}
                                    onChange={(e) =>
                                        updateField(
                                            "propertyName",
                                            e.target.value
                                        )
                                    }
                                    disabled={isPending}
                                    aria-invalid={errors.propertyName !== null}
                                    aria-describedby={
                                        errors.propertyName
                                            ? `${propNameId}-error`
                                            : undefined
                                    }
                                    autoComplete="off"
                                />
                                {errors.propertyName && (
                                    <p
                                        id={`${propNameId}-error`}
                                        role="alert"
                                        className="text-destructive text-xs"
                                    >
                                        {errors.propertyName}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-[1fr_80px] gap-2">
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor={valueId}>
                                        Value
                                        <span
                                            aria-hidden="true"
                                            className="text-destructive ml-0.5"
                                        >
                                            *
                                        </span>
                                    </Label>
                                    <Input
                                        ref={valueRef}
                                        id={valueId}
                                        placeholder="e.g. $1,000,000"
                                        value={form.value}
                                        onChange={(e) =>
                                            updateField("value", e.target.value)
                                        }
                                        disabled={isPending}
                                        aria-invalid={errors.value !== null}
                                        aria-describedby={
                                            errors.value
                                                ? `${valueId}-error`
                                                : undefined
                                        }
                                        autoComplete="off"
                                    />
                                    {errors.value && (
                                        <p
                                            id={`${valueId}-error`}
                                            role="alert"
                                            className="text-destructive text-xs"
                                        >
                                            {errors.value}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor={pageId}>
                                        Page
                                        <span className="text-muted-foreground ml-1 text-[11px] font-normal">
                                            opt.
                                        </span>
                                    </Label>
                                    <Input
                                        id={pageId}
                                        type="number"
                                        min={1}
                                        max={totalPages}
                                        placeholder={
                                            currentPage
                                                ? String(currentPage)
                                                : "—"
                                        }
                                        value={form.pageNumber}
                                        onChange={(e) => {
                                            const clamped = clampPageInput(
                                                e.target.value,
                                                totalPages
                                            );
                                            if (clamped !== null)
                                                updateField(
                                                    "pageNumber",
                                                    clamped
                                                );
                                        }}
                                        disabled={isPending}
                                        aria-invalid={
                                            errors.pageNumber !== null
                                        }
                                        aria-describedby={
                                            errors.pageNumber
                                                ? `${pageId}-error`
                                                : undefined
                                        }
                                        autoComplete="off"
                                        className="[appearance:textfield] text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    {errors.pageNumber && (
                                        <p
                                            id={`${pageId}-error`}
                                            role="alert"
                                            className="text-destructive text-xs"
                                        >
                                            {errors.pageNumber}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <Button
                                type="submit"
                                variant="default"
                                className="mt-1 w-full"
                                disabled={isPending}
                                aria-disabled={isPending}
                            >
                                {isPending ? (
                                    <>
                                        <Spinner className="mr-1.5" />
                                        Adding…
                                    </>
                                ) : (
                                    "Add"
                                )}
                            </Button>
                        </div>
                    </form>
                </PopoverContent>
            </Popover>
        </div>
    );
}
